import { ScreenplayParagraphType } from "./types";

export const PARAGRAPH_TYPE_LABELS: Record<ScreenplayParagraphType, string> = {
  scene_heading: "Scene Heading",
  action: "Action",
  character: "Character",
  dialogue: "Dialogue",
  parenthetical: "Parenthetical",
  shot: "Shot",
  transition: "Transition",
  general: "General",
};

export const PARAGRAPH_TYPE_OPTIONS: ScreenplayParagraphType[] = [
  "scene_heading",
  "action",
  "character",
  "dialogue",
  "parenthetical",
  "shot",
  "transition",
  "general",
];

/** Matches backend `GET /share/scripts/:token` (nanoid, 32 chars). */
export const SHARE_SCRIPT_TOKEN_PATTERN = /^[0-9a-zA-Z]{32}$/;

export const SCENE_DETAILS_INT_EXT_OPTIONS = ["INT.", "EXT."] as const;

/** Common scene heading time-of-day strings (uppercase), most-used first. Combobox allows any custom value. */
export const SCENE_HEADING_TIME_SUGGESTIONS = [
  "DAY",
  "NIGHT",
  "CONTINUOUS",
  "LATER",
  "SAME",
  "DAWN",
  "DUSK",
  "SUNRISE",
  "SUNSET",
  "MAGIC HOUR",
  "MOMENTS LATER",
  "THE NEXT DAY",
] as const;

export const ZOOM_LEVELS = [0.65, 0.75, 0.85, 0.9, 1, 1.1, 1.15, 1.25, 1.35] as const;
export const DEFAULT_ZOOM = 1;
export const MIN_ZOOM = ZOOM_LEVELS[0];
export const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

/** Index into `ZOOM_LEVELS` for a scale value (exact match or nearest step). */
export function nearestZoomLevelIndex(scale: number): number {
  const exact = ZOOM_LEVELS.indexOf(scale as (typeof ZOOM_LEVELS)[number]);
  if (exact >= 0) return exact;
  return ZOOM_LEVELS.reduce((bestIdx, z, i) => {
    const prev = ZOOM_LEVELS[bestIdx];
    return Math.abs(z - scale) < Math.abs(prev - scale) ? i : bestIdx;
  }, 0);
}

export const RECOVERY_KEY_PREFIX = "rivetai.script-editor.recovery";
export const RECOVERY_RETENTION_DAYS = 7;

/** Coalesce autosave + “save again after in-flight edits” into one PATCH after typing pauses (collab-friendly). */
export const DRAFT_SCENE_AUTOSAVE_DEBOUNCE_MS = 1200;

/**
 * Live collab: how long a client waits after the last edit that arrived from a peer before it
 * saves edits it did not make. Much longer than the typist's debounce, so the typist normally
 * saves first and this client then finds nothing new. It still saves if the typist's tab died.
 */
export const DRAFT_SCENE_REMOTE_EDIT_SAVE_DELAY_MS = 6000;

/**
 * Cap automatic recoverable retries (network / 429 / verify failure). Prevents idle PATCH storms
 * against the screenplay editing throttle (48/min) when a scene stays unsaved.
 */
export const DRAFT_SCENE_MAX_RECOVERABLE_SAVE_RETRIES = 5;

/**
 * Live collab: how many times in a row a body save may re-send on a peer's newer revision
 * (our Y doc already holds that revision) before we stop and show the conflict dialog.
 */
export const DRAFT_SCENE_MAX_COLLAB_REBASE_RETRIES = 3;

/** Dev-only: `sessionStorage.setItem(DEBUG_SCRIPT_EDITOR_SCENE_SESSION_KEY, "1")` then reload to log scene hydration. */
export const DEBUG_SCRIPT_EDITOR_SCENE_SESSION_KEY = "rivetai_debug_script_editor_scene";

/** Dialogue & action: Shift+Enter for soft breaks in-block; Enter always starts the next script block (TipTap). */
export function paragraphTypeAllowsSoftEnter(type: ScreenplayParagraphType): boolean {
  return type === "dialogue" || type === "action";
}

export function getScreenplayParagraphPlaceholder(type: ScreenplayParagraphType): string {
  const label = PARAGRAPH_TYPE_LABELS[type];
  if (paragraphTypeAllowsSoftEnter(type)) {
    return `${label} · Enter new script line · Shift+Enter line break in this block`;
  }
  return `${label} · Enter new script line · Shift+Enter or ⌘/Ctrl+Enter line break in this block`;
}

export const TOAST_MESSAGES = {
  saveError: "Could not save your edits. We'll retry automatically.",
  draftSaveVerifyError:
    "Could not confirm the save on the server. Your edits are still in the editor — try again.",
  draftSaveReadbackMismatch:
    "The server text did not match what we just saved. The editor was updated from the server copy. Save again if you still need to push changes.",
  saveConflict: "This scene was just updated elsewhere — review and resolve.",
  snapshotCreated: (label: string) => `Compare snapshot saved: \"${label}\"`,
  snapshotRestored: (label: string) => `Restored working draft from \"${label}\"`,
  snapshotError: "Could not save this snapshot. Please try again.",
  snapshotRestoreError: "Could not restore from this copy. Please try again.",
  exportStarted: (format: string) => `Generating ${format.toUpperCase()}…`,
  exportSuccess: (format: string) => `${format.toUpperCase()} ready to download`,
  exportError: (format: string) => `${format.toUpperCase()} export failed`,
  tagApplied: "Tag applied",
  tagAppliedEverywhere: "Tag applied across all scenes.",
  tagEverywhereUnavailable:
    "Tag across all scenes needs a saved paragraph. Save the scene or pick text in an existing line.",
  tagError: "Could not tag selection",
  commentCreated: "Comment added",
  commentUpdated: "Comment updated",
  commentDeleted: "Comment removed",
  commentResolved: "Comment resolved",
  commentUnresolved: "Comment unresolved",
  commentError: "Could not save comment",
  recoveryRestored: "Restored your unsaved edits from this device",
  reorderScenesError:
    "Could not save scene order. Check permissions and your connection, then try again.",
  sceneCreated: "New scene added",
  sceneCreateError: "Could not add scene. Try again.",
  sceneDeleted: "Scene removed",
  sceneDeleteError: "Could not delete scene. Try again.",
} as const;
